import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react'
import autoTable from 'jspdf-autotable';
import jsPDF from "jspdf";
import moment from "moment";

import { Table, TableBody, TableContainer, Paper } from "@mui/material";

import { callAddFontNirmalaBold } from '../../../styles/fonts/Nirmala-bold';
import { callAddFontNirmalaNormal } from '../../../styles/fonts/Nirmala-normal';
import { excelConfig, filterHeaderXlStyle, filterHeaderXlDataStyle } from '../../../configs/excelConfig';
import { fetchApi } from '../../../services/apiService';
import { hasAccess, jsPdfHeaderFooter, jsPdfDidParseCellCallback, jsPdfWillDrawCellCallback } from '../../../components/generics/helper';
import { Loader } from '../../../components/generics/Loader';
import { REPORT_TABLE_PAGINATION_ROWS_OPTIONS, EXPORT_PER_REQUEST_RECORD_LIMIT, REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED } from "../../../constants/commonConstants";
import { StyledTableRow, StyledTableCell } from "../../../components/Table/TableStyle";
import CommonTableHeaderRow from '../../../components/Table/CommonTableHeaderRow';
import CommonTablePagination from '../../../components/Table/CommonTablePagination';
import excelExport from '../../../services/exports/export-excel';
import TyreReportPopup from '../tyreReport/TyreReportPopup';

const RepairReportList = (props) => {
    const { repairReportFilters, loadData, setLoadData } = props
    const { enqueueSnackbar } = useSnackbar();

    const { selectedRolesRights = [], adminType = 0 } = useSelector((state) => state.login?.userData);
    const userToken = useSelector((state) => state.login.userToken);
    const userData = useSelector((state) => state.login.userData);

    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [perPageRecords, setPerPageRecords] = useState(REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED);

    const getRepairReportParams = (filters, page = 1, perPage = EXPORT_PER_REQUEST_RECORD_LIMIT) => {
        let params = {};
        if (filters?.searchVendorIds !== '') {
            params = { ...params, searchVendorIds: filters.searchVendorIds };
        }
        if (filters?.searchCategoryIds !== '') {
            params = { ...params, searchItemCategoryIds: filters?.searchCategoryIds };
        }
        if (filters?.searchSubCategoryIds !== '') {
            params = { ...params, searchItemSubCategoryIds: filters?.searchSubCategoryIds };
        }
        if (filters?.searchBrandIds !== '') {
            params = { ...params, searchItemBrandIds: filters?.searchBrandIds };
        }
        if (filters?.searchItemIds !== '') {
            params = { ...params, searchItemIds: filters?.searchItemIds };
        }
        params.fromDate = filters.fromDate;
        params.toDate = filters.toDate;
        params.formattedDate = 'Y';

        params.pageNo = page;
        params.perPageRecords = perPage;
        return params;
    };


    const generateRepairReportData = async (page = 0, rowsPerPage = perPageRecords) => {
        setIsLoading(true);
        try {
            const searchParams = getRepairReportParams(repairReportFilters, page + 1, rowsPerPage);

            const response = await fetchApi('reports/getRepairReportData', searchParams, userToken);
            setIsLoading(false);

            if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                if (response.data.repairItemReportData.length > 0) {
                    enqueueSnackbar('Repair Report Data Loaded Successfully', { variant: 'success', key: Date.now() });
                    setTableData(response?.data?.repairItemReportData);
                    setTotalRecords(response.data.totalRecords);
                } else {
                    enqueueSnackbar(response.data.message, { variant: 'error' });
                    setTableData([]);
                    setTotalRecords(0);
                }
            } else {
                enqueueSnackbar(response.errorMessage, { variant: 'error' });
                setTableData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            let message = 'Repair Report List Loading Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            setTableData([])
            setTotalRecords(0);
        } finally {
            setLoadData(false);
        }
    }

    const exportAllRepairReportData = async () => {
        const allRepairReportData = [];
        try {
            const params = getRepairReportParams(repairReportFilters);
            const totalPages = Math.ceil(totalRecords / params.perPageRecords);
            for (let page = 1; page <= totalPages; page++) {
                const pageParams = { ...params, pageNo: page };
                const response = await fetchApi('reports/getRepairReportData', pageParams, userToken);
                if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                    allRepairReportData.push(...response.data.repairItemReportData);
                }
            }
            return allRepairReportData;
        } catch (err) {
            let message = 'Repair Report List Export Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            return [];
        }
    }

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
        generateRepairReportData(newPage, perPageRecords);
    };
    const handleChangeRowsPerPage = (event) => {
        setPerPageRecords(parseInt(event.target.value, 10));
        setPageNo(0);
        generateRepairReportData(0, parseInt(event.target.value, 10));
    };

    const columnDefs = [
        { headerName: 'Voucher No.', pdfHead: 'Voucher No.', field: 'voucherNo', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: "120px", textAlign: 'left' }, allowSorting: false },
        { headerName: 'Invoice No.', pdfHead: 'InvoiceNo', field: 'invNo', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '150px' }, allowSorting: false },
        { headerName: 'Send Date', pdfHead: 'Send Date', field: 'sentDate', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Receive Date', pdfHead: 'Receive Date', field: 'receiveDate', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Vendor Name', pdfHead: 'Vendor', field: 'vendorName', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Category', pdfHead: 'Category', field: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '130px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Subcategory', pdfHead: 'Sub category', field: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Brand', pdfHead: 'Brand', field: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Item Name', pdfHead: 'Item Name', field: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Serial No', pdfHead: 'Serial No', field: 'serialNo', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false, },
        { headerName: 'Labour Chrgs with GST', pdfHead: 'Labour Chrgs with GST', field: 'labourTotal', xlCol: { dataType: 'currency', dataFormatter: null, width: 25 }, headerSx: { width: '120px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Spare Chrgs with GST', pdfHead: 'Spare Chrgs with GST', field: 'spareTotal', xlCol: { dataType: 'currency', dataFormatter: null, width: 25 }, headerSx: { width: '120px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Total Expenses with GST', pdfHead: 'Total Expenses with GST', field: 'totalAmount', xlCol: { dataType: 'currency', dataFormatter: null, width: 25 }, headerSx: { width: '120px', textAlign: 'right' }, allowSorting: false },
    ];

    const getFilterSectionData = () => {
        return {
            filterHeaderXlStyle,
            filterHeaderXlDataStyle,
            filterData: [
                {
                    columns: [
                        { header: 'From Date', dataKey: 'fromDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'To Date', dataKey: 'toDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Vendor', dataKey: 'vendorName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Category', dataKey: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Subcategory', dataKey: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Brand', dataKey: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Item Name', dataKey: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },

                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 28, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 28, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 43, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 43, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        5: { cellWidth: 43, halign: 'left', fontSize: 10 },
                        6: { cellWidth: 52, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            fromDate: moment(repairReportFilters.fromDate).format('DD-MMM-YY'),
                            toDate: moment(repairReportFilters.toDate).format('DD-MMM-YY'),
                            vendorName: repairReportFilters.searchVendorIds === "" ? 'All Vendors' : repairReportFilters.searchVendorIds.split(',').length > 1 ? `Multiple [ ${repairReportFilters.searchVendorIds.split(',').length} ]` : repairReportFilters?.searchVendorNames[0],
                            categoryTitle: repairReportFilters.searchCategoryIds === "" ? 'All Categories' : repairReportFilters.searchCategoryIds.split(',').length > 1 ? `Multiple [ ${repairReportFilters.searchCategoryIds.split(',').length} ]` : (repairReportFilters?.searchCategoryNames || [])[0],
                            subCategoryTitle: repairReportFilters.searchSubCategoryIds === "" ? 'All Sub Categories' : repairReportFilters.searchSubCategoryIds.split(',').length > 1 ? `Multiple [ ${repairReportFilters.searchSubCategoryIds.split(',').length} ]` : (repairReportFilters?.searchSubCategoryNames || [])[0],
                            brandName: repairReportFilters.searchBrandIds === "" ? 'All Brands' : repairReportFilters.searchBrandIds.split(',').length > 1 ? `Multiple [ ${repairReportFilters.searchBrandIds.split(',').length} ]` : (repairReportFilters?.searchBrandNames || [])[0],
                            itemName: repairReportFilters.searchItemIds === "" ? 'All Items' : repairReportFilters.searchItemIds.split(',').length > 1 ? `Multiple [ ${repairReportFilters.searchItemIds.split(',').length} ]` : repairReportFilters?.searchItemNames[0],
                        }
                    ]
                },
            ]
        }
    }
    const printReport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            setIsPdfLoaded(false);
            const pdfColumns = columnDefs.map((col) => {
                return { header: col.pdfHead, dataKey: col.field, xlCol: col.xlCol }
            });

            const allData = await exportAllRepairReportData();
            const fullData = allData.map(row => ({ ...row, isDataRow: true }));

            jsPDF.API.events.push(["addFonts", callAddFontNirmalaBold]);
            jsPDF.API.events.push(["addFonts", callAddFontNirmalaNormal]);
            const doc = new jsPDF({
                orientation: 'l',
                format: 'a4',
                compress: true,
            });
            var totalPagesExp = '{total_pages_count_string}';
            doc.autoTableSetDefaults({
                headStyles: { fontSize: 6, halign: 'left', fillColor: [30, 89, 167] },
            });
            doc.setProperties({
                title: `repairReport-${moment().format('DD-MMM-YY-HH-mm')}.pdf`,
                subject: "Repair Report",
                author: "Maven Tech Labs Pvt. Ltd.",
                keywords: "maven tech, Maintenance system, Repair Report",
                creator: "MTLPL",
            });
            autoTable(doc, {
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[0].columns,
                body: filterSectionData.filterData[0].data,
                columnStyles: filterSectionData.filterData[0].pdfColumnStyles,
                theme: 'grid',
                tableWidth: 'wrap',
            });
            doc.line(0, doc.lastAutoTable.finalY + 2, doc.getPageWidth(), doc.lastAutoTable.finalY + 2);
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 4,
                tableWidth: 'auto',
                margin: { top: 2, left: 5, right: 5, bottom: 5 },
                alternateRowStyles: {
                    fillColor: [255, 255, 255]
                },
                columns: pdfColumns,
                body: fullData,
                columnStyles: {
                    0: { minCellWidth: 20 },
                    1: { minCellWidth: 18 },
                    2: { minCellWidth: 20 },
                    3: { minCellWidth: 20 },
                    4: { minCellWidth: 30 },
                    5: { minCellWidth: 30 },
                    6: { minCellWidth: 30 },
                    7: { minCellWidth: 18 },
                    8: { minCellWidth: 30 },
                    9: { minCellWidth: 15 },
                    10: { minCellWidth: 18, halign: 'right' },
                    11: { minCellWidth: 18, halign: 'right' },
                    12: { minCellWidth: 20, halign: 'right' },
                },
                theme: 'grid',
                bodyStyles: {
                    fontSize: 6,
                    halign: 'left',
                },
                didParseCell: (data) => {
                    jsPdfDidParseCellCallback(data, pdfColumns,);
                },
                willDrawCell: (data) => {
                    jsPdfWillDrawCellCallback(data);
                },
                didDrawPage: function (data) {
                    data.settings.margin.top = 16;
                    const reportTitle = `Maintenance System | Repair Report`;
                    jsPdfHeaderFooter(data, reportTitle, userData, totalPagesExp);
                },
            },
            );
            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp)
            }
            setIsPdfLoaded(true);
            setTimeout(() => {
                // document.getElementById('output').data = doc.output('datauristring', `repairReport_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.pdf`);
                const blob = doc.output('blob');
                const url = URL.createObjectURL(blob);
                document.getElementById('output').data = url;
                setIsPdfLoading(false);
            }, 1000);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
            setIsPdfLoaded(false);
        }
    }

    const reportExcelExport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            const xlColumns = columnDefs.map((col) => {
                return { colHeader: col.headerName, field: col.field, xlCol: col.xlCol }
            });
            const reportTitle = `Maintenance System | Repair Report`
            const xlConfig = {
                ...excelConfig,
                columns: xlColumns,
                fileName: `Repair_Report_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.xlsx`,
                showFilterSection: true,
                filterSectionData,
                workSheetTitle: `Repair_Report`,
                headerFooter: {
                    firstHeader: `&L&14&K104FA2 ${reportTitle} &R&B&12 Company Name: ${userData.companyName}`,
                    firstFooter: `&L&12 Report By : ${userData.userFullName}  [ ${userData.branchName} ] &C&12 Report Time :  ${moment(new Date()).format("DD MMM YYYY, ddd, HH:mm")} &R&12 Page: &P of &N`
                },
                sheetHeaderData: [{
                    "caption": "Report Name :",
                    "value": `Repair Report`,
                    "colSpan": 7
                }, {
                    "caption": "Company Name :",
                    "value": userData.companyName,
                    "colSpan": 6
                }],
            };
            const allData = await exportAllRepairReportData();
            const fullData = allData.map(row => ({ ...row, isDataRow: true }));
            await excelExport.processExcel(xlConfig, fullData);
            setIsPdfLoading(false);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
        }
    }

    const backToScreenView = () => {
        setIsPdfLoaded(false);
    }

    useEffect(() => {
        if (loadData) {
            generateRepairReportData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    const handleMoreDetails = (e, row) => {
        e.stopPropagation();
        if (!row.serialNo) return;

        const srNoWiseItemHistoryFilters = {
            searchSerialNo: row.serialNo,
            searchCategoryName: row.categoryTitle || '-',
            searchSubCategoryName: row.subCategoryTitle || '-',
            searchItemId: row.itemId,
            searchItemName: row.itemName,
            searchBrandName: row.brandName || '-',
            searchVehBrandName: row.vehicleOemName || '-',
            searchVehModelName: row.vehicleModelName || '-',
        }
        setSelectedItemDetails(srNoWiseItemHistoryFilters);
        setShowMoreDetails(true);
    };

    const hideMoreDetails = () => {
        setShowMoreDetails(false);
        setSelectedItemDetails(null);
    }

    return (
        <>
            {isLoading && <div style={{ width: "100%", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
            {tableData && tableData.length > 0 &&
                <>
                    <div className='report--header--container'>
                        <div key="rr_report_name" className='report--for--heading'>Repair Report</div>
                        <div key="rr_report_action_container" className="report-export-button--container">
                            {!isPdfLoaded && <>
                                {hasAccess(selectedRolesRights, '4142', adminType) && <div key="rr_xls_btn" className="dashed--btn" onClick={() => reportExcelExport()}> EXPORT AS EXCEL </div>}
                                {hasAccess(selectedRolesRights, '4143', adminType) && <div key="rr_xpdf_btn" className="dashed--btn" onClick={() => printReport()}> EXPORT AS PDF </div>}
                                {hasAccess(selectedRolesRights, '4141', adminType) && <div key="rr_prn_btn" className="dashed--btn" onClick={() => printReport()}> PRINT </div>} </>}
                            {isPdfLoaded && <><div key="rr_backBtn" className="dashed--btn" onClick={() => backToScreenView()}> Back </div> </>}
                            {isPdfLoading && <div key="rr_loading_container" style={{ width: "100px", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                        </div>
                    </div>
                    {!isPdfLoaded && !isPdfLoading && (
                        <>
                            <TableContainer component={Paper} className="jc--scroll jobCrdPreviewModel" style={{ maxHeight: '75vh' }} >
                                <Table id="repairReport" stickyHeader aria-label="sticky table" sx={{ minWidth: 1500 }} >
                                    <CommonTableHeaderRow columnDefs={columnDefs} />
                                    <TableBody>
                                        {tableData && tableData.length > 0 ? (
                                            tableData.map((row, index) => {
                                                return (
                                                    <StyledTableRow key={`${row.rowType}-D-${index}`} className="grid" >
                                                        <StyledTableCell>{row.voucherNo}</StyledTableCell>
                                                        <StyledTableCell>{row.invNo}</StyledTableCell>
                                                        <StyledTableCell>{row.sentDate}</StyledTableCell>
                                                        <StyledTableCell>{row.receiveDate}</StyledTableCell>
                                                        <StyledTableCell>{row.vendorName}</StyledTableCell>
                                                        <StyledTableCell>{row.categoryTitle}</StyledTableCell>
                                                        <StyledTableCell>{row.subCategoryTitle}</StyledTableCell>
                                                        <StyledTableCell>{row.brandName}</StyledTableCell>
                                                        <StyledTableCell>{row.itemName}</StyledTableCell>
                                                        <StyledTableCell className="text" tabIndex={0} onClick={(e) => handleMoreDetails(e, row)}>{row.serialNo || ''}</StyledTableCell>
                                                        <StyledTableCell style={{ textAlign: "right" }}> {row.labourTotal} </StyledTableCell>
                                                        <StyledTableCell style={{ textAlign: "right" }}> {row.spareTotal} </StyledTableCell>
                                                        <StyledTableCell style={{ textAlign: "right" }}> {row.totalAmount} </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            })
                                        ) : (
                                            <StyledTableRow key="no-records">
                                                <StyledTableCell colSpan={columnDefs.length} align="center">
                                                    <div className="no--records">No Records</div>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className="report-pagination--container">
                                <CommonTablePagination
                                    rowsPerPageOption={REPORT_TABLE_PAGINATION_ROWS_OPTIONS}
                                    count={totalRecords}
                                    rowsPerPage={perPageRecords}
                                    className=""
                                    page={pageNo}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </div>
                        </>
                    )}
                </>
            }
            {isPdfLoaded && <object id="output" type="application/pdf" width="100%" height="600" title="MTLPL - Maintenance System - Repair Report" aria-label="MTLPL - Maintenance System - Repair Report"></object>}
            {showMoreDetails && selectedItemDetails && <TyreReportPopup handleModal={hideMoreDetails} selectedItemDetails={selectedItemDetails} />}
        </>
    );
}
export default RepairReportList;